export function make<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  classNames: string | string[] | null = null,
  attributes: Record<string, any> = {}
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tagName) as HTMLElementTagNameMap[K];

  if (Array.isArray(classNames)) {
    // Filter out falsy values and trim each class name
    const trimmed = classNames.filter(Boolean).map(c => c.trim());
    if (trimmed.length) el.classList.add(...trimmed);
  } else if (typeof classNames === 'string' && classNames.trim()) {
    const splitted = classNames.trim().split(/\s+/);
    if (splitted.length) el.classList.add(...splitted);
  }

  for (const attributeName in attributes) {
    if (Object.prototype.hasOwnProperty.call(attributes, attributeName)) {
      switch (attributeName) {
        case 'innerText':
        case 'innerHTML':
          (el as any)[attributeName] = attributes[attributeName];
          break;
        default:
          el.setAttribute(attributeName, String(attributes[attributeName]));
          break;
      }
    }
  }

  return el;
}

export function replaceMe(template: string, value: string): string {
  template = decodeURI(template);
  const pattern = /{\s*(\w+?)\s*}/g;
  return template
    .replace(pattern, value || '')
    .replace(/&amp;/g, '&');
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay = 300,
  immediate = false
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  type Debounced = ((...args: Parameters<T>) => void) & { cancel: () => void };

  const debounced = function (this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = undefined;
      if (!immediate) func.apply(this, args);
    };

    const callNow = immediate && timeout === undefined;

    if (timeout !== undefined) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, delay);

    if (callNow) {
      func.apply(this, args);
    }
  } as Debounced;

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  };

  return debounced;
}

/**
 * Parse an attribute string used in your codebase like:
 *   data-attr='{ source: ".title-input", delay: 100 }'
 * Accepts single quotes, unquoted keys and a relaxed JSON-like format.
 * Merges the parsed object over the provided defaults and returns typed result.
 */
export function parseOptions<T extends Record<string, any>>(rawAttr: string | null, defaults: T): T {
  if (!rawAttr) return { ...defaults };

  const raw = rawAttr.trim();
  // Try relaxed-JSON parse first (quote keys, convert single to double quotes)
  try {
    const jsonLike = raw
      .replace(/(['"])?([a-zA-Z0-9_]+)\1\s*:/g, '"$2":') // ensure keys are quoted
      .replace(/'/g, '"'); // convert single quotes to double quotes
    const parsed = JSON.parse(jsonLike) as Record<string, any>;
    return deepMerge(defaults, parsed as T);
  } catch {
    // Fallback parser: key:value pairs separated by commas, allows = or :
    const inner = raw.replace(/^\{/, '').replace(/\}$/, '');
    const parsed: Record<string, any> = {};
    const parts = inner.split(',').map(p => p.trim()).filter(Boolean);
    const parseValue = (v: string) => {
      const trimmed = v.trim();
      if (/^["'].*["']$/.test(trimmed)) return trimmed.replace(/^['"]|['"]$/g, '');
      if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === 'true';
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
      if (trimmed === 'null') return null;
      return trimmed;
    };

    for (const part of parts) {
      const kv = part.split(/[:=]/).map(x => x.trim());
      if (kv.length >= 2) {
        const key = kv[0];
        const val = kv.slice(1).join(':'); // keep colons in values if any
        parsed[key] = parseValue(val);
      }
    }

    return deepMerge(defaults, parsed as T);
  }
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const out: any = { ...(target as any) };
  for (const key of Object.keys(source || {})) {
    const val = (source as any)[key];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      out[key] = deepMerge(out[key] ?? {}, val);
    } else {
      out[key] = val;
    }
  }
  return out;
}

export function executeFunctionByName(functionName: string, context: any = window, ...args: any[]): any {
  if (!functionName) return undefined;

  const namespaces = functionName.split('.');
  const funcName = namespaces.pop();
  let ctx = context;

  for (const ns of namespaces) {
    if (ctx == null) return undefined;
    ctx = ctx[ns];
  }

  const fn = funcName ? ctx?.[funcName] : undefined;
  if (typeof fn === 'function') {
    return fn.apply(ctx, args);
  }

  return undefined;
}
